import React, { useCallback, useEffect, useState } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useFrancLanguageDetection, SafeDetectionResult } from '@fiji/common/src/hooks/useFrancLanguageDetection';
import { Info } from '@mui/icons-material';
import * as Colors from '@brightlayer-ui/colors';
import { useTranslation } from 'react-i18next';
import { hasInvalidChars } from '@fiji/common/src/hooks';

type LanguageTextFieldProps = TextFieldProps & {
    onLanguageDetected?: (language: string) => void;
    debounceDelay?: number; // Optional debounce delay in milliseconds
    isErrorText?: boolean; // Flag to indicate if the helper text is an error message
    handleTextError?: (hasError: boolean) => void;
    treatLeadingWhitespaceAsError?: boolean; // Opt-in: also treat a leading space as an invalid-chars error
};

const LanguageTextField: React.FC<LanguageTextFieldProps> = ({
    value,
    onChange,
    onLanguageDetected,
    helperText,
    isErrorText = false,
    debounceDelay = 500, // Default 500ms delay
    handleTextError,
    treatLeadingWhitespaceAsError = false,
    error: externalError,
    ...rest
}) => {
    // Mocked allowedLanguagesResponse data
    const { t } = useTranslation('COMMON');
    const [isTextFieldError, setIsTextFieldError] = useState(false);
    const { safeDetectLanguage } = useFrancLanguageDetection();

    // State to hold the debounced detection result
    const [detection, setDetection] = useState<SafeDetectionResult>(() => safeDetectLanguage(String(value ?? '')));

    // Sync error state from value so resets/initial population are always reflected
    useEffect(() => {
        const text = String(value ?? '');
        const hasError = hasInvalidChars(text) || (treatLeadingWhitespaceAsError && /^\s/.test(text));
        setIsTextFieldError(hasError);
        handleTextError?.(hasError);
    }, [value, handleTextError, treatLeadingWhitespaceAsError]);

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
                onChange(event);
            }
        },
        [onChange]
    );

    // Debounce detection: run 500ms after the latest value change
    useEffect(() => {
        const timer = window.setTimeout(() => {
            const newDetection = safeDetectLanguage(String(value ?? ''));
            setDetection(newDetection);
            if (onLanguageDetected) {
                onLanguageDetected(newDetection.mappedLocale);
            }
        }, debounceDelay);
        return () => {
            clearTimeout(timer);
        };
    }, [value, debounceDelay, safeDetectLanguage, onLanguageDetected]);

    // No extra cleanup needed; debounced effect cleans itself up

    const finalHelperText = detection.error ? detection.error : helperText ?? '';

    return (
        <TextField
            {...rest}
            error={isTextFieldError || !!externalError}
            value={value}
            onChange={handleChange}
            helperText={
                isTextFieldError ? (
                    t('TEXT_FIELD_INVALID_CHARS_ERROR')
                ) : isErrorText ? (
                    helperText
                ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1 }}>
                        {detection.error && (
                            <Info sx={{ fontSize: '1.5em', verticalAlign: 'middle', color: Colors.blue[500] }} />
                        )}
                        <span style={{ fontSize: 11 }}>{finalHelperText}</span>
                    </span>
                )
            }
            slotProps={{
                formHelperText: {
                    sx: {
                        color: isTextFieldError ? 'error.main' : detection.error ? Colors.blue[500] : 'text.secondary',
                        mb: 1,
                    },
                },
            }}
        />
    );
};

export default LanguageTextField;
